<script setup lang="ts">
import { Switch } from '@headlessui/vue'
import { uniqueId } from '@consumer/helpers/string'

const { id, label, labelOn, labelOff, hideLabel, size = 'large' } = defineProps<{
  id?: string
  label?: string
  labelOn?: string
  labelOff?: string
  hideLabel?: boolean
  size?: 'small' | 'medium' | 'large'
}>()

const modelValue = defineModel<boolean>({ default: false })

const inputId = computed(() => id ?? uniqueId('g-toggle-'))

const slots = useSlots()

const srOnlyLabel = computed(() => {
  if (modelValue.value) return labelOn || label || 'Toggle'
  return labelOff || label || 'Toggle'
})

const hasLabel = computed(() => label || labelOn || labelOff || hideLabel || slots.default)
</script>

<template>
  <div class="cursor-pointer flex ml-1 relative items-center g-toggle select-none">
    <div v-if="$slots.pre" class="flex-grow" @click="() => { modelValue = !modelValue }">
      <slot name="pre"/>
    </div>
    <Switch
      :id="inputId"
      v-model="modelValue"
      :class="[modelValue ? 'bg-primary-pale border-primary-base' : 'bg-grey-300 border-grey-400', size]"
      class="border-solid rounded-full border-1 h-8 w-15 relative inline-flex items-center g-toggle--switch"
    >
      <span class="sr-only">{{ srOnlyLabel }}</span>
      <span
        :class="[modelValue ?
          'translate-x-7 bg-primary-base g-toggle--switch--thumb--on' :
          'translate-x-1 bg-grey-400 g-toggle--switch--thumb--off', size]"
        class="bg-primary-base rounded-full h-6 transform transition w-6 inline-block g-toggle--switch--thumb"
      />
    </Switch>
    <label v-if="hasLabel" :for="inputId" :class="hideLabel ? 'sr-only' : 'cursor-pointer ml-2'">
      <slot>
        {{ (modelValue ? labelOn : labelOff) || label }}
      </slot>
    </label>
  </div>
</template>

<style lang="scss">
.g-toggle--switch {
  &.small {
    @apply h-5 w-8.25;
  }

  &.medium {
    @apply h-6 w-10;
  }

  .g-toggle--switch--thumb {
    &.small {
      @apply w-3 h-3;
    }

    &.medium {
      @apply w-4 h-4;
    }
  }

  .g-toggle--switch--thumb--on {
    &.small {
      @apply translate-x-4;
    }

    &.medium {
      @apply translate-x-5;
    }
  }
}
</style>
